import {
  usePreserveCallback
} from "./chunk-QOEP37JC.mjs";

// src/hooks/useIntersectionObserver.ts
import { noop } from "@teamsparta/utils";
import { useCallback, useEffect, useRef } from "react";
var defaultOptions = {
  root: null,
  rootMargin: "0px",
  threshold: 0
};
function useIntersectionObserver({
  onEnter = noop,
  onLeave = noop,
  onChange = noop,
  root = defaultOptions.root,
  rootMargin = defaultOptions.rootMargin,
  threshold = defaultOptions.threshold,
  unobserveOnEnter = false,
  unobserveOnLeave = false
}) {
  const ref = useRef(null);
  const observerRef = useRef(null);
  const isInitialRef = useRef(true);
  const onEnterCallback = usePreserveCallback(onEnter);
  const onLeaveCallback = usePreserveCallback(onLeave);
  const onChangeCallback = usePreserveCallback(onChange);
  const observe = useCallback(() => {
    if (!ref.current) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          onEnterCallback?.();
          onChangeCallback?.(entry);
          if (unobserveOnEnter) {
            observer.disconnect();
          }
        }
        if (entry && !entry.isIntersecting) {
          if (isInitialRef.current) {
            isInitialRef.current = false;
            return;
          }
          onLeaveCallback?.();
          onChange?.(entry);
          if (unobserveOnLeave) {
            observer.disconnect();
          }
        }
      },
      {
        root,
        rootMargin,
        threshold
      }
    );
    observerRef.current = observer;
    observer.observe(ref.current);
  }, [
    onChangeCallback,
    onEnterCallback,
    onLeaveCallback,
    root,
    rootMargin,
    threshold,
    unobserveOnLeave,
    unobserveOnEnter
  ]);
  const unobserve = useCallback(() => {
    if (!observerRef.current) {
      return;
    }
    observerRef.current.disconnect();
    observerRef.current = null;
    isInitialRef.current = true;
  }, []);
  useEffect(() => {
    observe();
    return unobserve;
  }, [observe, unobserve]);
  return { ref, observe, unobserve };
}

export {
  useIntersectionObserver
};
